import { CollectorDTO } from "api";
import { SnackbarMessage } from "notistack";
import { MaybeNull } from "types";

/**
 * Builds a formatted string representation of a collector.
 *
 * - Combines the utility type name, account number, and meter number into a single string.
 * - If the collector is disabled (`enabled === false`), prefixes the string with `[De-activated]`.
 * - Uses `"-"` as a fallback for missing values.
 *
 * @param {CollectorDTO} params - The collector data used to construct the display string.
 * @param {string} [params.accountNumber] - The collector's account number.
 * @param {string} [params.meterNumber] - The collector's meter number.
 * @param {string} [params.utilityTypeName] - The name of the utility type (e.g., Electricity, Water).
 * @param {boolean} [params.enabled] - Indicates whether the collector is active.
 * @returns {string} A formatted string describing the collector, with `[De-activated]` prefix if disabled.
 *
 * @example
 * getCollectorString({
 *   accountNumber: "12345",
 *   meterNumber: "67890",
 *   utilityTypeName: "Electricity",
 *   enabled: true
 * });
 * // Returns: "Electricity / 12345 / 67890"
 *
 * @example
 * getCollectorString({
 *   accountNumber: "12345",
 *   meterNumber: "67890",
 *   utilityTypeName: "Water",
 *   enabled: false
 * });
 * // Returns: "[De-activated] Water / 12345 / 67890"
 */
export const getCollectorString = ({
  accountNumber,
  meterNumber,
  utilityTypeName,
  enabled,
}: CollectorDTO) => {
  if (enabled !== undefined && enabled === false) {
    return `[De-activated] ${utilityTypeName || "-"} / ${accountNumber || "-"} / ${meterNumber || "-"}`;
  }
  return `${utilityTypeName || "-"} / ${accountNumber || "-"} / ${meterNumber || "-"}`;
};

/**
 * Compares a collector string with a collector object to determine if they represent the same data.
 *
 * - Uses `getCollectorString()` to generate a standardized string from the collector object,
 *   then checks equality with the provided string.
 *
 * @param {string} collectorString - The string to compare against the collector.
 * @param {CollectorDTO} collector - The collector data used to generate a comparison string.
 * @returns {boolean} `true` if the generated collector string matches the provided one, otherwise `false`.
 *
 * @example
 * const collector = {
 *   accountNumber: "12345",
 *   meterNumber: "67890",
 *   utilityTypeName: "Electricity",
 *   enabled: true
 * };
 *
 * compareCollector("Electricity / 12345 / 67890", collector);
 * // Returns: true
 */
export const compareCollector = (
  collectorString: string,
  collector: CollectorDTO,
): boolean => collectorString === getCollectorString(collector);

/**
 * Sends a formatted notification message through the provided notification handler.
 *
 * - If a `name` is provided, it appends it to the message in the format: `"message: name!"`.
 * - If `name` is `null` or `undefined`, it sends `"message!"` instead.
 *
 * @param {(message: SnackbarMessage) => void} notify - The function responsible for displaying or handling the notification.
 * @param {string} message - The main message to display in the notification.
 * @param {MaybeNull<string>} name - An optional name or label to include in the message.
 * @returns {void}
 *
 * @example
 * sendUtilityBillNotificationMessage(showSnackbar, "Bill successfully uploaded", "Water");
 * // Displays: "Bill successfully uploaded: Water!"
 *
 * @example
 * sendUtilityBillNotificationMessage(showSnackbar, "Bill successfully uploaded", null);
 * // Displays: "Bill successfully uploaded!"
 */
export const sendUtilityBillNotificationMessage = (
  notify: (message: SnackbarMessage) => void,
  message: string,
  name: MaybeNull<string>,
) => notify(name ? `${message}: ${name}!` : `${message}!`);
